import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { getComicById } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const comic = id ? getComicById(id) : undefined;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <i className="ri-emotion-sad-line text-4xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-heading text-foreground-900 mb-2">漫画未找到</h2>
            <p className="text-sm text-foreground-600 mb-4">抱歉，您要查看的漫画不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryLabels: Record<string, string> = {
    hanman: '韩漫',
    riman: '日漫',
    guoman: '国漫',
  };

  return (
    <>
      <Navbar />
      <main>
        {/* Breadcrumb */}
        <div className="pt-20 pb-0 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <nav className="flex items-center gap-2 text-xs text-foreground-500 py-3 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
              <div className="w-3 h-3 flex items-center justify-center">
                <i className="ri-arrow-right-s-line"></i>
              </div>
              <Link to={`/category/${comic.category}`} className="hover:text-primary-600 transition-colors cursor-pointer">
                {categoryLabels[comic.category]}
              </Link>
              <div className="w-3 h-3 flex items-center justify-center">
                <i className="ri-arrow-right-s-line"></i>
              </div>
              <span className="text-foreground-800 font-medium">{comic.title}</span>
            </nav>
          </div>
        </div>

        {/* Comic Detail */}
        <section className="py-8 md:py-12 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row gap-6 md:gap-10">
              {/* Cover */}
              <div className="flex-shrink-0 w-full md:w-[280px]">
                <div className="aspect-[5/7] rounded-lg overflow-hidden">
                  <img
                    src={comic.coverImage}
                    alt={comic.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              {/* Info */}
              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-3">{comic.title}</h1>
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-primary-100 text-primary-700">
                    {categoryLabels[comic.category]}
                  </span>
                  <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                    comic.status === '连载中' ? 'bg-accent-100 text-accent-800' : 'bg-foreground-100 text-foreground-700'
                  }`}>
                    {comic.status}
                  </span>
                </div>

                <div className="flex flex-wrap items-center gap-4 mb-5 text-sm text-foreground-600">
                  <div className="flex items-center gap-1.5">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-user-line"></i>
                    </div>
                    <span>{comic.author}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-accent-500"></i>
                    </div>
                    <span className="font-semibold text-foreground-900">{comic.rating}</span>
                    <span className="text-foreground-500">分</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-eye-line"></i>
                    </div>
                    <span>{comic.views >= 10000 ? `${(comic.views / 10000).toFixed(1)}万` : comic.views} 次阅读</span>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mb-5">
                  {comic.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-700">
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="bg-background-100 rounded-lg p-4 mb-5">
                  <h3 className="text-sm font-semibold text-foreground-900 mb-2">作品简介</h3>
                  <p className="text-sm text-foreground-700 leading-relaxed">{comic.description}</p>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <button className="px-8 py-3 rounded-full bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-play-circle-line"></i>
                    </div>
                    开始阅读 {comic.chapters.length > 0 ? comic.chapters[0].title : ''}
                  </button>
                  <button className="px-8 py-3 rounded-full border border-background-300 text-foreground-700 text-sm font-medium hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-bookmark-line"></i>
                    </div>
                    加入收藏
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Chapters List */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-900">章节列表</h2>
              <div className="flex items-center gap-2">
                <button className="px-3 py-1.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700 cursor-pointer whitespace-nowrap">
                  正序
                </button>
                <button className="px-3 py-1.5 rounded-full text-xs font-medium text-foreground-600 hover:bg-background-200 cursor-pointer whitespace-nowrap">
                  倒序
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {comic.chapters.map((chapter) => (
                <div
                  key={chapter.id}
                  className="flex items-center justify-between p-3.5 rounded-lg bg-background-50 hover:bg-background-200/50 transition-colors cursor-pointer group"
                >
                  <span className="text-sm text-foreground-800 group-hover:text-primary-600 transition-colors truncate">
                    {chapter.title}
                  </span>
                  <span className="text-xs text-foreground-400 flex-shrink-0 ml-3">{chapter.updateDate}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}